import {
  Anchor,
  Badge,
  Card,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { Link } from 'react-router';
import { GOREV_NESNE_HEDEF_HARITASI } from '@isg/validation';
import type { TenantOverview } from '@isg/shared-types';
import { islerimSayaci, useIslerim, useIkincilOkuma, useSuresiGecenler } from '../api/queries';
import type { IslerimKalemi } from '../api/queries';
import { ISLERIM_ETIKET } from '../i18n/tr';
import { DashboardBloklari } from './DashboardScreen';
import { IdentityCard } from './IdentityCard';
import { Rozet } from '../sozlesme';
import { tarihGoster } from '../lib/format';

/**
 * Genel bakış (F1·S10; sadeleşme S10 gezinti kararı 05.08.2026; ✏️ **TUR 12 —
 * DASHBOARD BU SAYFADIR**, Kapsam §26): kullanıcının düştüğü tenant + SSO
 * kimliği + kendi kuyruğu + holding/işyeri göstergeleri. F0·S4 "doğru tenant'a düşer" kabul yüzeyi
 * burasıdır — test kancaları (tenant-result/tenant-ad/tenant-company) korunur;
 * tenant id SATIRI KALDIRILDI (teknik artık — e2e ayrımı tenant ADI üzerinden).
 * Tenant burada seçilmez, seçilemez: sunucu kimliği DB eşlemesinden çözer.
 */
export function HomeScreen({ tenant }: { tenant: TenantOverview }): React.JSX.Element {
  return (
    <Stack>
      <Title order={3}>Genel bakış</Title>
      {/* ✏️ 07.10.2026 — İŞLERİM + SÜRESİ GEÇENLER YAN YANA (Genel bakış düzeni):
          iki sütunlu ızgara, dar ekranda alt alta. İki blok aynı eksende
          DEĞİLDİR (kuyruk · durum özeti — IA §3/8) ve birleşmez; yan yana
          durmaları yalnız yerleşimdir. "Hemen başla" düğmesi YOK — kalemler
          zaten bağlantıdır. Süresi geçenler bloğu gösterilemiyorsa (kapı
          kapalı) ızgara tek sütun kalır. */}
      <SimpleGrid cols={{ base: 1, md: 2 }} data-testid="genel-bakis-ust">
        <IslerimBlogu />
        <SuresiGecenlerBlogu />
      </SimpleGrid>
      {/* ⚠️ **TUR 12 / K11 — DASHBOARD *"Genel bakış"*IN YERİNE GEÇTİ**
          (Kapsam §26.7): yeni bir menü satırı ya da rota AÇILMADI, bloklar
          bu sayfaya indi. *İşlerim* kendi sayfası olarak kalır.
          ⚠️ SIRA BİLİNÇLİ: önce *"ne yapmam gerek"* (kuyruk + geçmiş vade),
          sonra *"nasıl gidiyoruz"* (ölçüm). Bu sayfa bir yöneticinin değil,
          **herkesin** indiği sayfadır. */}
      <DashboardBloklari />
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Card withBorder padding="md" data-testid="tenant-result">
          <Text size="sm" fw={600} mb="xs">
            Bağlı olduğunuz yapı
          </Text>
          <Table variant="vertical" layout="fixed" withRowBorders={false}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th w={120}>Tenant</Table.Th>
                <Table.Td data-testid="tenant-ad">{tenant.ad}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          <Text size="sm" fw={600} mt="md" mb="xs">
            Şirketler
          </Text>
          <List size="sm" data-testid="tenant-companies">
            {tenant.companies.map((company) => (
              <List.Item key={company.id} data-testid="tenant-company">
                {company.ad}
              </List.Item>
            ))}
          </List>
        </Card>
        <IdentityCard />
      </SimpleGrid>
    </Stack>
  );
}

/**
 * İŞLERİM BLOĞU (F4·S5·B1 — T1b): menü ANLATIM SIRASINI izlediği için günlük
 * iş menünün altına düşer; T1'in bu bedeli burada karşılanır.
 *
 * ⚠️ **SAYI ÜST BAR SAYACIYLA AYNI YÜKLEMDEN GELİR** (`useIslerim` — IA §3/2):
 * üst bar "3" derken burası 5 satır gösterirse kullanıcı ikisine de güvenmez.
 * İkinci bir sorgu/sayım YAZMA.
 *
 * ⚠️ Bu blok bir **KUYRUKTUR** (kimlik ekseni: *"bunlar senin işin"*).
 * B2'nin *"süresi geçenler"* bloğu bir **DURUM ÖZETİDİR** (konu ekseni) ve
 * onunla BİRLEŞTİRİLMEZ (IA §3/8).
 */
/**
 * ⚠️ ÜÇ KAYNAK, ÜÇ ETİKET (0d — §2.40). Blok eskiden iki kollu bir üçlemeyle
 * (`kaynak === 'aksiyon' ? 'Aksiyon' : 'Onay'`) çiziliyordu ve periyodik
 * GÖREV satırları *"Onay"* diye etiketleniyordu — kullanıcı olmayan bir
 * onayı arıyordu. `Record` kullanılıyor ki yeni bir kuyruk eklendiğinde
 * TypeScript etiketi yazmayı ZORUNLU kılsın (sessiz yanlış etiket yerine
 * derleme hatası).
 */
const ISLERIM_KAYNAK_ETIKET: Record<IslerimKalemi['kaynak'], string> = {
  aksiyon: 'Aksiyon',
  onay: 'Onay',
  gorev: 'Görev',
  onayim: 'Kapanış onayı',
  katilimci: 'Katılımcı onayı',
};

function IslerimBlogu(): React.JSX.Element | null {
  const islerim = useIslerim();
  return (
    <Card withBorder padding="md" data-testid="islerim-blogu">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Text fw={600}>{ISLERIM_ETIKET}</Text>
          <Badge variant="light" data-testid="islerim-blok-sayac">
            {islerimSayaci(islerim)}
          </Badge>
        </Group>
        <Anchor component={Link} to="/islerim" size="sm">
          Hepsini gör →
        </Anchor>
      </Group>
      {islerim.yukleniyor ? null : islerim.toplam === 0 ? (
        <Text size="sm" c="dimmed">
          Açık işiniz yok.
        </Text>
      ) : (
        <List size="sm" data-testid="islerim-blok-listesi">
          {islerim.kalemler.slice(0, 5).map((kalem) => (
            <List.Item key={`${kalem.kaynak}-${kalem.id}`} data-testid="islerim-blok-kalemi">
              <Anchor component={Link} to={kalem.yol} size="sm">
                {ISLERIM_KAYNAK_ETIKET[kalem.kaynak]} · {kalem.baslik}
              </Anchor>
            </List.Item>
          ))}
        </List>
      )}
    </Card>
  );
}

/**
 * "SÜRESİ GEÇENLER" BLOĞU (F4·S5·B2 — T8; Kapsam §22.6a + §20.3a).
 *
 * ⚠️ **BU BİR DURUM ÖZETİDİR, KUYRUK DEĞİL.** *"12 ekipmanın periyodik
 * kontrolü geçti"* der ve **KONU** ekseninde konuşur; İşlerim *"bunlar senin
 * işin"* der ve **KİMLİK** ekseninde konuşur. İkisi aynı veriyi farklı
 * eksende okur ve **BİRLEŞTİRİLMEZ** (IA §3/8): birleşirse ya blok
 * kişiselleşir (**G7 ihlali** — sistem kimin geciktirdiğini söylemeye başlar)
 * ya İşlerim kişisel olmaktan çıkar.
 *
 * ⚠️ **BLOK KİŞİ ADI TAŞIMAZ.** Sunucu yanıtında kişi alanı yoktur; buraya
 * *"Ahmet 3 görevi geciktirdi"* yazan kod, bildirim tarafında kapatılan
 * kapıyı ekran tarafında **sessizce geri açar.**
 *
 * ⚠️ **BAHÇE DENETİMİ SUNUCUDADIR** ve görev listesiyle AYNI filtreden gelir
 * (§2.16/B3: ekran ikinci filtre yazmaz) — dar bahçeli uzman yalnız kendi
 * işyerinin sayısını görür.
 */
function SuresiGecenlerBlogu(): React.JSX.Element | null {
  const ozet = useSuresiGecenler();
  const gorevListesi = useIkincilOkuma('gorevListesi');
  // ✏️ SESSİZ 403 PAKETİ (29.09.2026): eskiden yetkisiz şapkada uç ÇAĞRILIR ve
  // 403 yutulurdu (*"blok sessizce çizilmez"*). Artık kapı sunucudan okunur ve
  // kapalıysa uç HİÇ çağrılmaz (`useSuresiGecenler`); blok çizilmez.
  if (!ozet.data) return null;
  // ✏️ 07.10.2026 — DURUM KARTI (Genel bakış düzeni): kalem VARSA kırmızı kenar
  // + kırmızı sayı rozeti; BOŞSA nötr kart ("Süresi geçen kalem yok."). Renk =
  // durum; kalem yoksa durum yok. Eskiden boş blok hiç çizilmiyordu — yan yana
  // yerleşimde İşlerim'in karşısı boş kalır ve kullanıcı bloğun var olduğunu
  // bilmezdi. Kapı kapalıysa (uç çağrılmıyor) blok yine çizilmez (yukarısı).
  const kalemVar = ozet.data.toplam > 0;

  return (
    <Card
      withBorder
      padding="md"
      className="isg-durum-kart"
      data-durum={kalemVar ? 'var' : 'yok'}
      data-testid="suresi-gecenler-blogu"
    >
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Text fw={600}>Süresi geçenler</Text>
          {/* ~~Kırmızı = kalem var (07.10.2026 — `engel`).~~ ✏️ REVİZE 08.10.2026
              (ürün sahibi): kalemler OPERASYONEL kontroldür, PTW'yi bloke
              etmez — kırmızı yalnız `engel` için ayrılmıştır; kart durumunu
              G7'nin `gecikmis` (amber) tokeniyle söyler. Kenarlık da aynı
              token (index.css `.isg-durum-kart`). */}
          <Rozet anlam={kalemVar ? 'gecikmis' : 'bilgi'} testId="suresi-gecenler-sayac">
            {String(ozet.data.toplam)}
          </Rozet>
        </Group>
        {/* Görev listesini okuyamayan şapkada (alan sorumlusu) bağ çizilmez —
            gideceği sekme 403 verirdi. Satırlar nesnenin kendisine götürür. */}
        {gorevListesi.acik && (
          <Anchor component={Link} to="/ekipman/gorevler" size="sm">
            Görev listesine git →
          </Anchor>
        )}
      </Group>
      {!kalemVar ? (
        <Text size="sm" c="dimmed" data-testid="suresi-gecenler-bos">
          Süresi geçen kalem yok.
        </Text>
      ) : (
        <List size="sm" data-testid="suresi-gecenler-listesi">
          {ozet.data.satirlar.map((satir) => (
            <List.Item
              key={`${satir.nesne.id}-${satir.kalemAd}`}
              data-testid="suresi-gecenler-kalemi"
            >
              {/* ✏️ BULGU 103 (K-103a/b): satır birden çok gecikmiş dönemi toplar —
                hedefi tek görev değil gecikmenin NESNESİDİR (haritadan). */}
              <Anchor
                component={Link}
                to={GOREV_NESNE_HEDEF_HARITASI[satir.nesne.tip](satir.nesne.id)}
                size="sm"
                data-testid="suresi-gecenler-bagi"
              >
                {satir.nesne.ad} · {satir.kalemAd}
              </Anchor>{' '}
              — {satir.gecikmisDonem} dönem gecikti (en eski vade {tarihGoster(satir.enEskiVade)})
            </List.Item>
          ))}
        </List>
      )}
    </Card>
  );
}
